import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PricingSection = ({ formData, setFormData, errors }) => {
  const pricingTypeOptions = [
    { value: 'free', label: 'Free' },
    { value: 'fixed', label: 'Fixed Price' },
    { value: 'range', label: 'Price Range' },
    { value: 'donation', label: 'Donation Based' },
    { value: 'contact', label: 'Contact for Pricing' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev?.pricing,
        [field]: value
      }
    }));
  };

  const handlePricingTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev?.pricing,
        type: value,
        // Reset price fields when type changes
        fixedPrice: '',
        minPrice: '',
        maxPrice: '',
        suggestedDonation: ''
      }
    }));
  };

  const formatPrice = (value) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value?.replace(/[^\d.]/g, '');
    return numericValue;
  };

  return (
    <div className="bg-card rounded-lg p-6 space-y-6">
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-heading font-semibold text-foreground">Pricing Information</h2>
        <p className="text-sm text-muted-foreground mt-1">Set your pricing structure</p>
      </div>
      <div className="space-y-6">
        <div>
          <Select
            label="Pricing Type"
            placeholder="Select pricing model"
            options={pricingTypeOptions}
            value={formData?.pricing?.type}
            onChange={handlePricingTypeChange}
            error={errors?.['pricing.type']}
            required
          />
        </div>

        {formData?.pricing?.type === 'fixed' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Input
                label="Price (£)"
                type="text"
                placeholder="25.00"
                value={formData?.pricing?.fixedPrice}
                onChange={(e) => handleInputChange('fixedPrice', formatPrice(e?.target?.value))}
                error={errors?.['pricing.fixedPrice']}
                required
              />
            </div>
          </div>
        )}

        {formData?.pricing?.type === 'range' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Input
                label="Minimum Price (£)"
                type="text"
                placeholder="15.00"
                value={formData?.pricing?.minPrice}
                onChange={(e) => handleInputChange('minPrice', formatPrice(e?.target?.value))}
                error={errors?.['pricing.minPrice']}
                required
              />
            </div>
            <div>
              <Input
                label="Maximum Price (£)"
                type="text"
                placeholder="35.00"
                value={formData?.pricing?.maxPrice}
                onChange={(e) => handleInputChange('maxPrice', formatPrice(e?.target?.value))}
                error={errors?.['pricing.maxPrice']}
                required
              />
            </div>
          </div>
        )}

        {formData?.pricing?.type === 'donation' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <Input
                label="Suggested Donation (£)"
                type="text"
                placeholder="10.00"
                value={formData?.pricing?.suggestedDonation}
                onChange={(e) => handleInputChange('suggestedDonation', formatPrice(e?.target?.value))}
                description="Optional suggested amount"
              />
            </div>
          </div>
        )}

        {formData?.pricing?.type === 'free' && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-success rounded-full flex items-center justify-center mt-0.5">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              <div>
                <h3 className="font-body font-medium text-foreground">Free Service</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Your listing will be marked as free to attend. This can help attract more participants.
                </p>
              </div>
            </div>
          </div>
        )}

        {formData?.pricing?.type === 'contact' && (
          <div className="bg-accent/20 border border-accent/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-accent rounded-full flex items-center justify-center mt-0.5">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
              <div>
                <h3 className="font-body font-medium text-foreground">Contact for Pricing</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Visitors will be directed to contact you directly for pricing information.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <Input
            label="Currency Note"
            type="text"
            placeholder="e.g., Per session, Per month, Per course"
            value={formData?.pricing?.currencyNote}
            onChange={(e) => handleInputChange('currencyNote', e?.target?.value)}
            description="Clarify what the price covers"
          />

          <Checkbox
            label="Early bird discount available"
            description="Tick if you offer discounted rates for early bookings"
            checked={formData?.pricing?.earlyBird}
            onChange={(e) => handleInputChange('earlyBird', e?.target?.checked)}
          />

          <Checkbox
            label="Concession rates available"
            description="Tick if you offer reduced rates for students, seniors, or low income"
            checked={formData?.pricing?.concessions}
            onChange={(e) => handleInputChange('concessions', e?.target?.checked)}
          />
        </div>
      </div>
    </div>
  );
};

export default PricingSection;